// @flow

import React from 'react';
import { connect } from 'react-redux';
import changeCarouselSlotIndex from 'app/common/redux/actions/pin/carousel';
import getCarouselData from 'app/common/react/components/common/Carousel/utils/getCarouselData';
import { getCommerceAuxData } from 'app/common/lib/CommerceUtil';
import { SingleTreatmentExp } from 'app/common/lib/experiment';
import { track } from 'app/common/lib/react/analytics';
import { type CarouselPinData } from 'app/packages/pinterest-flow-types/pin';
import { type State as Pin } from 'app/common/redux/reducers/pin';
import { type State as ReduxState } from 'app/common/redux/reducers/types';
import { useTrackingParameters } from 'app/common/react/components/PinRep/TrackingParametersContext';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { Box, Touchable } from 'gestalt';
import { compose, type Dispatch } from 'redux';

type ReduxProps = {|
  carouselData: ?CarouselPinData,
  changeCarouselSlotIndex: (pinId: string, newIndex: number) => void,
  pin?: Pin,
|};

type OwnProps = {|
  pinId: string,
|};

type HOCProps = {|
  requestContext: RequestContextType,
|};

type ConnectedProps = {|
  ...ReduxProps,
  ...OwnProps,
|};

type Props = {|
  ...ReduxProps,
  ...OwnProps,
  ...HOCProps,
|};

const SMALL_ELLIPSIS_DOT_DIMENSION_PX = 6;
const LARGE_ELLIPSIS_DOT_DIMENSION_PX = 10;

export function CarouselEllipsis({
  carouselData,
  changeCarouselSlotIndex,
  pin,
  pinId,
  requestContext,
}: Props) {
  const { componentType, viewParameter, viewType } = useTrackingParameters();

  const { carousel_slots: carouselSlots = [], id: carouselId, index: carouselIndex } =
    carouselData || {};

  const inCarouselPipExperiment = new SingleTreatmentExp(
    requestContext,
    'web_carousel_pip_improvements',
  ).enabled();
  requestContext.experiments.flush();
  const ELLIPSIS_DOT_DIMENSION_PX = inCarouselPipExperiment
    ? LARGE_ELLIPSIS_DOT_DIMENSION_PX
    : SMALL_ELLIPSIS_DOT_DIMENSION_PX;

  const buildOnTouchHandler = (index: number) => ({ event }) => {
    event.preventDefault();
    event.stopPropagation();

    if (changeCarouselSlotIndex !== undefined) {
      changeCarouselSlotIndex(pinId, index);

      track(PCONST.thrift.EventType.SWIPE, {
        component: componentType,
        eventData: {
          pinCarouselSlotEventData: {
            carouselDataId: Number(carouselId),
            carouselSlotId: carouselSlots[carouselIndex] && Number(carouselSlots[carouselIndex].id),
            carouselSlotIndex: carouselIndex,
            toCarouselSlotIndex: index,
          },
        },
        objectId: pinId,
        view: viewType,
        viewParameter,
        ...(getCommerceAuxData(pin) || {}),
      });
    }
  };

  return (
    <Box
      display="flex"
      dangerouslySetInlineStyle={{ __style: { marginLeft: -3, marginRight: -3 } }}
    >
      {carouselSlots.map((item, i) => (
        <Box
          key={pinId + i}
          dangerouslySetInlineStyle={{ __style: { marginLeft: 3, marginRight: 3 } }}
        >
          <Touchable fullWidth={false} shape="circle" onTouch={buildOnTouchHandler(i)}>
            <Box
              data-test-id="ellipsis-size"
              height={ELLIPSIS_DOT_DIMENSION_PX}
              width={ELLIPSIS_DOT_DIMENSION_PX}
              dangerouslySetInlineStyle={{
                __style: { backgroundColor: i === carouselIndex ? '#555' : 'lightGray' },
              }}
              shape="circle"
            />
          </Touchable>
        </Box>
      ))}
    </Box>
  );
}

function mapStateToProps({ pins }: ReduxState, { pinId }: OwnProps) {
  const pin = pins[pinId] || {};
  return {
    carouselData: getCarouselData(pin),
    pin,
  };
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
  return {
    changeCarouselSlotIndex: (pinId, newIndex) =>
      dispatch(changeCarouselSlotIndex(pinId, newIndex)),
  };
}

export default compose(
  connect<ConnectedProps, OwnProps, _, _, _, _>(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withRequestContext,
)(CarouselEllipsis);
